/**
 * Loads the sign-in page status message once per page load (edge-bff GET /bff/config/login-notice,
 * public, `Cache-Control: no-store`). Shared by every surface that renders `<tb-login-screen>`:
 * toybank-app and backoffice bind `notice()`; only the debug wrapper also reads `debugWrapper()`
 * to compose its environment advisory.
 *
 * Fails silent by design: the message must never block or delay sign-in. Any error (401 from a
 * confined session, 5xx, network), a timeout, or a body of the wrong shape (the mocked backend
 * answers unknown GETs with `200 []`) leaves both signals null -- no toast, no console output,
 * no sign-out redirect (TB_SUPPRESS_AUTH_REDIRECT).
 */
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { LoginNotice, TB_SUPPRESS_AUTH_REDIRECT, TB_TIMEOUT_MS, parseLoginNotice } from '@toybank/ui';
import { timeout } from 'rxjs';

export const LOGIN_NOTICE_URL = '/bff/config/login-notice';
export const LOGIN_NOTICE_TIMEOUT_MS = 4_000;
const ENV_MODE = /^[a-z]{1,16}$/;

export interface LoginNoticeDebugWrapper {
  readonly envMode: string;
}

function parseDebugWrapper(raw: unknown): LoginNoticeDebugWrapper | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const envMode = (raw as Record<string, unknown>)['envMode'];
  return typeof envMode === 'string' && ENV_MODE.test(envMode) ? { envMode } : null;
}

@Injectable({ providedIn: 'root' })
export class LoginNoticeService {
  private readonly http = inject(HttpClient);
  private requested = false;
  private readonly noticeState = signal<LoginNotice | null>(null);
  private readonly debugWrapperState = signal<LoginNoticeDebugWrapper | null>(null);

  /** The validated operator message, or null. */
  readonly notice = this.noticeState.asReadonly();
  /** Non-null only when the server says the debug wrapper is active for this host. */
  readonly debugWrapper = this.debugWrapperState.asReadonly();

  /** Issues the GET at most once per page load; later calls are no-ops. */
  ensureLoaded(): void {
    if (this.requested) return;
    this.requested = true;
    const context = new HttpContext()
      .set(TB_SUPPRESS_AUTH_REDIRECT, true)
      .set(TB_TIMEOUT_MS, LOGIN_NOTICE_TIMEOUT_MS);
    this.http
      .get<unknown>(LOGIN_NOTICE_URL, { context })
      // Also bounded here: the interceptor that honours TB_TIMEOUT_MS is not installed everywhere.
      .pipe(timeout({ first: LOGIN_NOTICE_TIMEOUT_MS }))
      .subscribe({
        next: (body) => {
          if (typeof body !== 'object' || body === null || Array.isArray(body)) return;
          const record = body as Record<string, unknown>;
          this.noticeState.set(parseLoginNotice(record['notice']));
          this.debugWrapperState.set(parseDebugWrapper(record['debugWrapper']));
        },
        error: () => {
          // Silent: no message is the correct outcome when the status cannot be read.
        },
      });
  }
}
